import { useCallback } from 'react'

import type { ExternalIdentifier } from 'types/writer'

import { api } from '@disco/state-management/api/index'
import { useUpdateRightsHolderMutation } from '@disco/state-management/api/rights-holders'

import { useDispatch } from 'hooks/use-redux'

import { areExternalIdentifiersEqual } from '../utils/identifier-validation'

import { useRightsNewModelEnabled } from './use-rights-new-model-enabled'

type RightsEntityType =
  | 'PRO'
  | 'Publisher'
  | 'Writer'
  | 'Label'
  | 'Rightsholder'

interface UseUpdateRightsEntityOptions {
  /** The type of entity being updated */
  entityType: RightsEntityType
  /** The old API thunk action to dispatch when feature flag is OFF */
  updateThunk?: (
    id: number,
    name: string,
    externalIdentifiers?: ExternalIdentifier[],
  ) => any
}

/**
 * Hook for updating rights entities (PRO, Publisher, Writer)
 *
 * When rightsNewModelEnabled is ON, uses the new RTK Query mutation.
 * When OFF, uses the provided Redux thunk action.
 * When the migration is complete (SLAM-1413), update this hook to only
 * use the new API, then remove the feature flag check entirely.
 *
 * @example
 * ```tsx
 * const updateEntity = useUpdateRightsEntity({
 *   entityType: 'Writer',
 *   updateThunk: updateWriter,
 * })
 *
 * const handleSave = async (name, identifiers) => {
 *   await updateEntity(entityId, name, identifiers)
 * }
 * ```
 */
export function useUpdateRightsEntity({
  entityType,
  updateThunk,
}: UseUpdateRightsEntityOptions) {
  const dispatch = useDispatch()
  const rightsNewModelEnabled = useRightsNewModelEnabled()
  const [updateRightsHolder] = useUpdateRightsHolderMutation()

  return useCallback(
    async (
      id: number,
      name: string,
      externalIdentifiers?: ExternalIdentifier[],
      originalExternalIdentifiers?: ExternalIdentifier[],
    ): Promise<void> => {
      const hasExternalIdentifiersChanged = !areExternalIdentifiersEqual(
        externalIdentifiers,
        originalExternalIdentifiers,
      )

      if (rightsNewModelEnabled) {
        // Use new RTK Query API - cache invalidation is handled by the mutation
        const data: {
          title: string
          external_identifiers?: ExternalIdentifier[]
        } = {
          title: name,
        }

        if (hasExternalIdentifiersChanged) {
          data.external_identifiers = externalIdentifiers
        }

        await updateRightsHolder({
          id,
          data,
        }).unwrap()
      } else {
        // Use old Redux thunk API
        if (updateThunk) {
          await dispatch(
            updateThunk(
              id,
              name,
              hasExternalIdentifiersChanged ? externalIdentifiers : undefined,
            ),
          )
        }

        // Writers need explicit cache invalidation for the old API
        if (entityType === 'Writer') {
          dispatch(api.util.invalidateTags(['Writers']))
        }
      }
    },
    [
      dispatch,
      rightsNewModelEnabled,
      updateRightsHolder,
      entityType,
      updateThunk,
    ],
  )
}
